import { Drawer, Table } from '@/components/commons';
import { CogevoStampImageService } from '@/services/cogevoStampImage';
import { ENDPOINT } from '@/services/endpoint';
import { FALLBACK_URL } from '@/services/image';
import { IResponse, IResponseError } from '@/types/common';
import {
  ICreateCogevoStampImageRequest,
  IUpdateCogevoStampImageRequest,
} from '@/types/request/cogevoStampImage';
import { IGetDetailCogevoStampImageResponse } from '@/types/response/cogevoStampImage';
import {
  handleErrorCommon,
  notificationMessage,
  notificationModal,
  NOTIFY_STATUS,
} from '@/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Image, Tag, TableColumnsType } from 'antd';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { CogevoStampImageForm } from '../Drawers/CogevoStampImageForm';
import { formSchemaFn } from '../Drawers/CogevoStampImageForm/formSchemaFn';

interface IProps {
  title?: string;
}

type FormValues = {
  isDefault: boolean;
  month?: string;
  url: string;
  sortOrder: number;
  isActive: boolean;
};

const defaultValues: FormValues = {
  isDefault: false,
  month: '',
  url: '',
  sortOrder: 0,
  isActive: true,
};

/**
 * CogEvo 脳トレスタンプカードの報酬イラスト管理。
 * 25 日プレイで獲得するイラストを月ごとに登録する。対象月の行が無い場合は既定イラストが使われる。
 */
export const CogevoStampImageList: React.FC<IProps> = ({ title }) => {
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [detail, setDetail] = useState<IGetDetailCogevoStampImageResponse>();

  const { data, refetch, isFetching } = useQuery(
    [ENDPOINT.COGEVO_STAMP_IMAGE.GET_LIST],
    () => CogevoStampImageService.getList(),
  );

  // 登録済み枚数（月別）。フォームで「次は N 枚目」を案内するために使う
  const countByMonth = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of data?.data ?? []) {
      if (!item.month || item.isDefault) continue;
      counts.set(item.month, (counts.get(item.month) ?? 0) + 1);
    }

    return counts;
  }, [data]);

  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(formSchemaFn(!!currentId)),
    reValidateMode: 'onChange',
  });
  const { handleSubmit, reset, setError } = methods;

  const handleCancel = () => {
    setOpenDrawer(false);
    setCurrentId(null);
    setDetail(undefined);
    reset(defaultValues);
  };

  const { mutate: createImage, isLoading: isLoadingCreate } = useMutation(
    (body: ICreateCogevoStampImageRequest) =>
      CogevoStampImageService.create(body),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'イラストを登録しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
      },
    },
  );

  const { mutate: updateImage, isLoading: isLoadingUpdate } = useMutation(
    ({ body, id }: { body: IUpdateCogevoStampImageRequest; id: number }) =>
      CogevoStampImageService.update(body, id),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'イラストを更新しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
      },
    },
  );

  const { mutate: deleteImage, isLoading: isLoadingDelete } = useMutation(
    (id: number) => CogevoStampImageService.delete(id),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'イラストを削除しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
      },
    },
  );

  const { isFetching: isFetchingDetail } = useQuery(
    [ENDPOINT.COGEVO_STAMP_IMAGE.GET_DETAIL.replace(':id', String(currentId))],
    () => CogevoStampImageService.getDetail(currentId as number),
    {
      enabled: !!currentId,
      onSuccess: (res: IResponse<IGetDetailCogevoStampImageResponse>) => {
        setDetail(res?.data);
        reset({
          isDefault: res?.data?.isDefault,
          month: res?.data?.month ?? '',
          url: res?.data?.url ?? '',
          sortOrder: res?.data?.sortOrder ?? 0,
          isActive: res?.data?.isActive,
        });
      },
    },
  );

  const onSubmit = (values: FormValues) => {
    if (currentId) {
      // 対象月（imageCode）は変更できない
      updateImage({
        body: {
          url: values.url,
          sortOrder: values.sortOrder,
          isActive: values.isActive,
        },
        id: currentId,
      });

      return;
    }

    createImage({
      ...(values.isDefault ? {} : { month: values.month }),
      url: values.url,
      sortOrder: values.sortOrder,
      isActive: values.isActive,
    });
  };

  const handleDelete = () => {
    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '削除しますか？',
      maskClosable: false,
      onOk: () => deleteImage(detail!.id),
      okText: '削除する',
      cancelText: 'キャンセル',
      wrapClassName: 'delete-modal-wrapper',
    });
  };

  const columns: TableColumnsType<any> = [
    {
      title: 'イラスト',
      dataIndex: 'url',
      align: 'center',
      width: 100,
      render: (value) =>
        value ? (
          <Image
            width={64}
            height={64}
            src={value}
            preview={false}
            fallback={FALLBACK_URL}
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        ) : (
          <span className='text-gray-400'>なし</span>
        ),
    },
    {
      title: '対象月',
      dataIndex: 'month',
      align: 'center',
      width: 120,
      render: (value, record) =>
        record.isDefault ? <Tag color='blue'>既定</Tag> : value,
    },
    {
      title: '何枚目',
      dataIndex: 'orderInMonth',
      align: 'center',
      width: 90,
      render: (value, record) =>
        record.isDefault ? (
          <span className='text-gray-400'>―</span>
        ) : (
          `${value}枚目`
        ),
    },
    {
      title: 'imageCode',
      dataIndex: 'imageCode',
      align: 'left',
      width: 200,
    },
    {
      title: '状態',
      dataIndex: 'isActive',
      align: 'center',
      width: 100,
      render: (value) =>
        value ? <Tag color='green'>有効</Tag> : <Tag>無効</Tag>,
    },
    {
      title: '並び順',
      dataIndex: 'sortOrder',
      align: 'center',
      width: 80,
    },
    {
      title: '更新日',
      dataIndex: 'updatedAt',
      align: 'center',
      width: 160,
      render: (value) =>
        value ? moment(value).format('YYYY年MM月DD日 HH:mm') : '',
    },
  ];

  return (
    <div className='p-post-list'>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px]'>
        <div className='bg-white rounded p-4 text-sm leading-6'>
          25日分プレイするごとに1枚のイラストを獲得します。イラストはカードの最初のスタンプを押した月のものが使われ、
          <span className='font-semibold'>
            同じ月に複数枚登録すると、その月の1枚目・2枚目…のカードに並び順の小さいものから順に使われます
          </span>
          。カードより枚数が少ない場合は最後の1枚が繰り返され、その月の登録が無い場合は既定イラストが使われます。
          <br />
          一度カードに割り当てられたイラストは、後から差し替えても変わりません。
        </div>
        <div className='flex justify-end'>
          <Button
            type='primary'
            onClick={() => {
              setOpenDrawer(true);
              reset(defaultValues);
            }}
          >
            新規作成
          </Button>
        </div>
        <Table
          loading={isFetching}
          scroll={{ x: 800 }}
          bordered
          rowKey='id'
          columns={columns}
          dataSource={data?.data}
          pagination={false}
          onRow={(record) => ({
            onClick: () => {
              setOpenDrawer(true);
              setCurrentId(record.id);
            },
          })}
          locale={{ emptyText: 'イラストはまだありません' }}
        />
      </div>
      <Drawer
        title={!currentId ? 'スタンプ画像 新規作成' : 'スタンプ画像 詳細'}
        onOk={handleSubmit((values) => onSubmit(values as FormValues))}
        onCancel={handleCancel}
        onDelete={handleDelete}
        maskClosable={false}
        open={openDrawer}
        okText={!currentId ? '保存' : '更新'}
        loading={
          currentId
            ? isFetchingDetail || isLoadingUpdate || isLoadingDelete
            : isLoadingCreate
        }
        // 既定イラストが無いと対象月未登録のカードで画像を出せないため削除させない
        isDeleted={!!currentId && !detail?.isDefault}
      >
        <FormProvider {...methods}>
          <CogevoStampImageForm
            isLoading={isFetchingDetail}
            isEdit={!!currentId}
            countByMonth={countByMonth}
          />
        </FormProvider>
      </Drawer>
    </div>
  );
};
